import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteApproval } from '@/hooks/mutations/approval';
import { useGetApprovalById } from '@/hooks/queries';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';

import CreateModal from './CreateModal';
import UpdateModal from './UpdateModal';

type Props = {
  open: boolean;
  onClose?: () => void;
  approvalId: null | string;
  parentId?: string;
  readOnly?: boolean;
};

const ApprovalModal: FC<Props> = ({
  open,
  onClose,
  approvalId,
  parentId,
  readOnly,
}) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'approvals' });
  const { deleteApproval } = useDeleteApproval();

  // Error toasts are surfaced by the hook's internal `useErrorNotification`
  // (createQueryHook) for both the tRPC and GraphQL branches — no manual
  // onError needed here (would double-notify).
  const { data, loading } = useGetApprovalById({
    queryArgs: { id: approvalId ?? '' },
    shouldSkip: !approvalId,
  });

  const approval = data?.approval;

  const onDelete = useDeleteResultNotification({
    entityName: t('entity_name'),
    asyncAction: async () => {
      if (!approvalId) {
        return false;
      }
      await deleteApproval({
        Id: approvalId,
      });
      onClose?.();

      return true;
    },
  });

  return (
    <>
      {loading || !open ? null : approval ? (
        <UpdateModal
          approval={approval}
          onDismiss={onClose}
          onDelete={
            approval
              ? async () => {
                  await onDelete();
                }
              : undefined
          }
          readOnly={readOnly}
          parentId={parentId}
        />
      ) : (
        <CreateModal
          onDismiss={onClose}
          parentId={parentId}
          readOnly={readOnly}
        />
      )}
    </>
  );
};

export default ApprovalModal;
